import i18n from "i18n";
import {
  StatusesEnums,
  UserRole,
} from "components/Reports/reportAssets/assets";
import { ReportModel } from "models/ReportModel";
import { useGetTextForCodeValue } from "hooks/useDataCodeValue";
import { useLocationForReportType } from "hooks/useLocationForReportType";
import { convertISOtoDMYHM } from "./convertISO";

// D-08 (Pitfall 6): match the status COLOR by the stable StatusesEnums `id`,
// not by a display string. Status DISPLAY labels are now localized (they reflow
// per language via the enum lookup helper), so matching on the label would
// silently return null in non-Hebrew languages. The numeric id is language- and
// role-independent, so the color stays correct regardless of the active language.
// The `role` argument is retained for signature compatibility with callers.
export const getStatusColor = (statusId: number, _role?) => {
  switch (statusId) {
    case StatusesEnums.REPORT_COMPLETION_BY_VOLUNTEER.id:
      return "#50fed8";
    case StatusesEnums.VOLUNTEER_REPAIR_PENDING.id:
      return "#50FED8";
    case StatusesEnums.NEW.id:
      return "#fe5086";
    case StatusesEnums.REPEAT_FROM_PENDING.id:
      return "#5c78ff";
    case StatusesEnums.SENIOR_CONTROL_NEW.id:
      return "#5C78FF";
    case StatusesEnums.SENIOR_CONTROL_PENDING.id:
      return "#fe8c50";
    case StatusesEnums.SENIOR_CONTROL_FROM_AWAITING.id:
      return "#8650fe";
    case StatusesEnums.CENTER_CONTROL_NEW.id:
      return "#fe50c5";
    case StatusesEnums.CENTER_CONTROL_PENDING.id:
      return "#50fe8c";
    case StatusesEnums.CENTER_CONTROL_FROM_AWAITING.id:
      return "#c550fe";
    case StatusesEnums.AWAITING_A_SECOND_INSPECTION.id:
      return "#c654ff";
    case StatusesEnums.COMPLETED.id:
      return "#FE5086";
    case StatusesEnums.AWAITING_FEEDBACK.id:
      return "#4ffd6c";
    case StatusesEnums.REPORT_IN_TEST_MODE.id:
      return "#FF7518";
    case StatusesEnums.WITHDRWAN_BY_CONTROL_CENTER.id:
      return "#edfa78";
    case StatusesEnums.START_PROCESS_SEND_TO_POLICE.id:
      return "#f5b942";
    case StatusesEnums.REGULAR_CONTOL_FROM_SENIOR.id:
      return "#8938c7";
    default:
      return null;
  }
};
const FormatReportDetails = (report: ReportModel, role) => {
  const isInprogress = useLocationForReportType() === "inprogress";

  const location = () => {
    if (report.reportIsInterstate) {
      return [
        {
          label: i18n.t("labelRoad"),
          value: report.reportRoad,
        },
        {
          label: i18n.t("labelKm"),
          value: report.reportLocationKm,
        },
      ];
    }
    return [
      {
        label: i18n.t("city"),
        value: report.cityName,
      },
      {
        label: i18n.t("street"),
        value: report.streetName,
      },
      {
        label: i18n.t("houseNumber"),
        value: report?.houseNumber,
      },
    ];
  };
  const controllerName = () => {
    if (role !== UserRole.REGULAR) {
      return {
        label:
          role === UserRole.SENIOR
            ? i18n.t("labelControllerName")
            : i18n.t("labelController"),
        value: report.regularControllerName,
      };
    }
    return null;
  };

  const seniorControllerName = () => {
    if (role === UserRole.CONTROLCENTER || role === UserRole.SENIOR_WATCH) {
      return {
        label: i18n.t("labelSeniorControllerName"),
        value: report.seniorControllerName,
      };
    }
    return null;
  };

  const reportDetails = [
    {
      label: i18n.t("labelDate"),
      value: convertISOtoDMYHM(report.createdOn),
    },
    {
      label:
        role !== UserRole.SENIOR_WATCH
          ? i18n.t("labelReportName")
          : i18n.t("reporter"),
      value: report.userAppName || i18n.t("anonymous"),
    },
    controllerName(),
    seniorControllerName(),
    ...location(),
    {
      label: i18n.t("offenceTypeName"),
      value: useGetTextForCodeValue(
        isInprogress && report.volunteerOffence
          ? report.volunteerOffence
          : report.offenceTypeID,
        "offences"
      ),
    },
    {
      label: i18n.t("labelDescision"),
      value: useGetTextForCodeValue(report.decisionTypeCode, "decisions"),
    },
    {
      label: i18n.t("labelLastUpdate"),
      value: convertISOtoDMYHM(report.updateOn),
    },
    {
      label: i18n.t("labelVehicleType"),
      value: getModelType(report.modelType),
    },
  ];

  return reportDetails;
};

export default FormatReportDetails;

export const getModelType = (name: string) => {
  if (name === null || name === "null") return i18n.t("noData");
  if (typeof name !== "string") {
    throw new TypeError("Expected string input");
  }
  const lowercaseName = name.toLowerCase();

  if (lowercaseName === "p" || lowercaseName === "private") {
    return i18n.t("private");
  }
  if (lowercaseName === "m" || lowercaseName === "commercial") {
    return i18n.t("commercial");
  }
  return name;
};
